import { Card, CardDescription, CardTitle } from "@/components/ui/Card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/Modal";
import { X, Star, Sparkles, FileText, ArrowRight } from "lucide-react";
import ModuleGate from "@/permissions/ModuleGate";
import { MODULES } from "@/permissions/modules";
import useModuleAccess from "@/hooks/useModuleAccess";

const PostJobModal = ({ open, onClose, setActiveModal, setAiStep }) => {
  // A tenant without the AI Job Description module does not reach this chooser
  // — the Post a New Job button opens the manual form for them, because one
  // option is not a choice. The gate below stays as the lock, and `aiEnabled`
  // keeps the layout whole if the chooser is ever opened another way: the
  // recommended banner goes with the card it labels, and the grid drops to one
  // column so the remaining card does not sit next to a hole.
  const { isEnabled: aiEnabled } = useModuleAccess(MODULES.AI_JD_IMPROVER);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose(); // always go back to Job Listing
      }}
      className="border border-red-500"
    >
      <DialogContent className="!p-0 overflow-hidden w-[90%] max-w-md sm:max-w-lg">
        {" "}
        {/* HEADER */}
        <DialogHeader className="relative px-4 py-4 sm:px-6 sm:py-5" style={{ zoom: 0.95 }}>
          <div className="pr-10">
            <DialogTitle className="text-base sm:text-xl font-semibold text-text-primary">Post a New Job</DialogTitle>

            <DialogDescription className="text-xs sm:text-sm text-text-primary mt-1">Choose how you'd like to create your job posting.</DialogDescription>
          </div>

          <DialogClose className="absolute right-3 top-3 sm:right-7 sm:top-1/2 sm:-translate-y-1/2 w-8 h-8 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>
        {/* CONTENT */}
        <div className="px-4 pb-5 pt-3 md:p-6" style={{ zoom: 0.95 }}>
          {/* Recommended */}
          {aiEnabled && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-t-xl bg-background-tertiary/60">
              <Star size={14} className="text-yellow-400 fill-yellow-400" />
              <span className="text-xs text-text-primary">Recommended - Saves Time</span>
            </div>
          )}

          {/* Cards */}
          <div className={`grid grid-cols-1 gap-3 md:gap-5 ${aiEnabled ? "lg:grid-cols-2" : ""}`}>
            {/* AI CARD */}
            <ModuleGate module={MODULES.AI_JD_IMPROVER}>
            <Card
              onClick={() => {
                setAiStep(1);
                setActiveModal("AI");
              }}
              className="bg-background-tertiary/60 border-none transition-all duration-200 cursor-pointer p-5 rounded-xl rounded-tl-none flex flex-col h-full"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-md bg-background-icon/80 flex items-center justify-center">
                  <Sparkles size={18} className="text-text-primary" />
                </div>

                <h3 className="text-sm font-semibold text-text-primary">AI-Powered Job Description</h3>
              </div>

              <div className="flex-1">
                <p className="text-xs text-text-primary leading-relaxed">
                  Turn your job requirements into clear, attractive postings that appeal to top talent.
                </p>
              </div>

              <p className="text-blue-500 text-sm mt-5 flex items-center gap-1">
                Choose this mode <ArrowRight size={14} />
              </p>
            </Card>
            </ModuleGate>

            {/* MANUAL CARD */}
            <Card
              onClick={() => {
                setActiveModal("MANUAL");
              }}
              className="bg-background-tertiary/60 border-none transition-all duration-200 cursor-pointer p-5 rounded-xl flex flex-col h-full"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-md bg-background-icon/80 flex items-center justify-center">
                  <FileText size={18} className="text-text-primary" />
                </div>

                <h3 className="text-sm font-semibold text-text-primary">Manual Posting</h3>
              </div>

              <div className="flex-1">
                <p className="text-xs text-text-primary leading-relaxed">Enter the required job information to generate a structured job description.</p>
              </div>

              <p className="text-blue-500 text-sm mt-5 flex items-center gap-1">
                Choose this mode <ArrowRight size={14} />
              </p>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostJobModal;
